import axios from 'axios';

// En producción (build), usar ruta relativa al mismo servidor
// En desarrollo, usar localhost:5000
const API_URL = process.env.NODE_ENV === 'production' 
  ? '/api'  // Ruta relativa cuando se sirve desde el mismo servidor
  : (process.env.REACT_APP_API_URL || '/api');

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para agregar token a todas las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar errores de autenticación
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const getFilenameFromDisposition = (disposition) => {
  const match = String(disposition || '').match(/filename="?([^"]+)"?/i);
  return match ? match[1] : '';
};

const openBlobResponse = (response, fallbackName, download = false) => {
  const contentType = response.headers?.['content-type'] || 'application/octet-stream';
  const filename = getFilenameFromDisposition(response.headers?.['content-disposition']) || fallbackName || 'archivo';
  const blob = new Blob([response.data], { type: contentType });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  if (download) {
    link.download = filename;
  } else {
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
  }

  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

// ============================================
// AUTH SERVICES
// ============================================
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    if (response.data.success) {
      localStorage.setItem('token', response.data.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.data.doctor));
    }
    return response.data;
  },

  changePassword: async (currentPassword, newPassword) => {
    const response = await api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
    if (response.data.success && response.data.data?.token) {
      localStorage.setItem('token', response.data.data.token);
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  },

  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  // Obtener información completa del doctor (incluyendo cédula para recetas)
  getDoctorInfo: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  getDoctors: async () => {
    const response = await api.get('/auth/doctors');
    return response.data;
  },

  getPublicDoctors: async () => {
    const response = await api.get('/auth/public-doctors');
    return response.data;
  },
};

// ============================================
// PATIENT SERVICES
// ============================================
export const patientService = {
  create: async (patientData) => {
    const response = await api.post('/patients', patientData);
    return response.data;
  },

  getAll: async (page = 1, limit = 20, search = '') => {
    const response = await api.get('/patients', {
      params: { page, limit, search },
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/patients/${id}`);
    return response.data;
  },

  update: async (id, patientData) => {
    const response = await api.put(`/patients/${id}`, patientData);
    return response.data;
  },

  searchByName: async (query, limit = 10) => {
    const response = await api.get('/patients/search/quick', {
      params: { q: query, limit },
    });
    return response.data;
  },

  getHistory: async (id) => {
    const response = await api.get(`/patients/${id}/history`);
    return response.data;
  },

  getAuditTrail: async (id) => {
    const response = await api.get(`/patients/${id}/audit`);
    return response.data;
  },
};

// ============================================
// CONSULTATION SERVICES
// ============================================
export const consultationService = {
  create: async (consultationData) => {
    const response = await api.post('/consultations', consultationData);
    return response.data;
  },

  getAll: async (page = 1, limit = 20, paciente_id = null) => {
    const response = await api.get('/consultations', {
      params: { page, limit, paciente_id },
    });
    return response.data;
  },

  getById: async (id) => {
    const response = await api.get(`/consultations/${id}`);
    return response.data;
  },

  update: async (id, consultationData) => {
    const response = await api.put(`/consultations/${id}`, consultationData);
    return response.data;
  },

  getAuditTrail: async (id) => {
    const response = await api.get(`/consultations/${id}/audit`);
    return response.data;
  },

  getStats: async () => {
    const response = await api.get('/consultations/stats');
    return response.data;
  },

  // Autoguardado
  autosave: async (sessionKey, formData) => {
    const response = await api.post('/consultations/autosave', {
      session_key: sessionKey,
      form_data: formData,
    });
    return response.data;
  },

  getActiveDrafts: async () => {
    const response = await api.get('/consultations/autosave');
    return response.data;
  },

  getAutosave: async (sessionKey) => {
    const response = await api.get(`/consultations/autosave/${sessionKey}`);
    return response.data;
  },

  deleteAutosave: async (sessionKey) => {
    const response = await api.delete(`/consultations/autosave/${sessionKey}`);
    return response.data;
  },

  // Búsqueda CIE-10
  searchCIE10: async (query) => {
    const response = await api.get('/consultations/cie10/search', {
      params: { q: query, limit: 20 },
    });
    return response.data;
  },
};

// ============================================
// AUDIT SERVICES (bitácora de cambios — solo admin / dr.oscar)
// ============================================
export const auditService = {
  getLog: async ({ page = 1, limit = 25, tabla = '', doctor_id = '', from = '', to = '' } = {}) => {
    const response = await api.get('/audit', {
      params: { page, limit, tabla, doctor_id, from, to },
    });
    return response.data;
  },

  getDoctors: async () => {
    const response = await api.get('/audit/doctors');
    return response.data;
  },
};

// ============================================
// LABORATORY SERVICES
// ============================================
export const laboratoryService = {
  getAll: async (estado = '', patient_id = null) => {
    const response = await api.get('/laboratories', {
      params: { estado, patient_id },
    });
    return response.data;
  },

  create: async ({ paciente_id, consulta_id = null, detalle_solicitud }) => {
    const response = await api.post('/laboratories', {
      paciente_id,
      consulta_id,
      detalle_solicitud,
    });
    return response.data;
  },

  updateStatus: async (id, estado) => {
    const response = await api.patch(`/laboratories/${id}`, { estado });
    return response.data;
  },

  getDocuments: async (id) => {
    const response = await api.get(`/laboratories/${id}/documentos`);
    return response.data;
  },

  uploadDocument: async (id, file, description, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) formData.append('descripcion', description);
    const response = await api.post(`/laboratories/${id}/documentos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
    return response.data;
  },

  deleteDocument: async (docId) => {
    const response = await api.delete(`/laboratories/documentos/${docId}`);
    return response.data;
  },

  getPatientHistory: async (pacienteId) => {
    const response = await api.get(`/laboratories/paciente/${pacienteId}/historial`);
    return response.data;
  },

  openDocument: async (docId, fallbackName, download = false) => {
    const response = await api.get(`/laboratories/documentos/${docId}/file`, {
      params: { download: download ? '1' : undefined },
      responseType: 'blob',
    });
    openBlobResponse(response, fallbackName, download);
  },
};

// ============================================
// DOCTOR SKETCH SERVICES
// ============================================
export const doctorSketchService = {
  list: async (doctorId = null) => {
    const response = await api.get('/doctor-sketches', {
      params: doctorId ? { doctor_id: doctorId } : undefined,
    });
    return response.data;
  },

  upload: async ({ file, doctor_id, nombre_visible, descripcion }, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (doctor_id) formData.append('doctor_id', doctor_id);
    if (nombre_visible) formData.append('nombre_visible', nombre_visible);
    if (descripcion) formData.append('descripcion', descripcion);

    const response = await api.post('/doctor-sketches', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => onProgress(Math.round((event.loaded * 100) / event.total))
        : undefined,
    });
    return response.data;
  },

  update: async (id, payload) => {
    const response = await api.patch(`/doctor-sketches/${id}`, payload);
    return response.data;
  },

  replace: async (id, { file, doctor_id, nombre_visible, descripcion }, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (doctor_id) formData.append('doctor_id', doctor_id);
    if (nombre_visible) formData.append('nombre_visible', nombre_visible);
    if (descripcion !== undefined) formData.append('descripcion', descripcion);

    const response = await api.post(`/doctor-sketches/${id}/replace`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (event) => onProgress(Math.round((event.loaded * 100) / event.total))
        : undefined,
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/doctor-sketches/${id}`);
    return response.data;
  },

  open: async (id, fallbackName, download = false) => {
    const response = await api.get(`/doctor-sketches/${id}/file`, {
      params: { download: download ? '1' : undefined },
      responseType: 'blob',
    });
    openBlobResponse(response, fallbackName, download);
  },
};

// ============================================
// UPLOAD SERVICES
// ============================================
export const uploadService = {
  upload: async (file, description, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (description) {
      formData.append('descripcion', description);
    }
    const response = await api.post('/uploads', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress
        ? (e) => onProgress(Math.round((e.loaded * 100) / e.total))
        : undefined,
    });
    return response.data;
  },

  list: async () => {
    const response = await api.get('/uploads');
    return response.data;
  },

  delete: async (id) => {
    const response = await api.delete(`/uploads/${id}`);
    return response.data;
  },

  open: async (id, fallbackName, download = true) => {
    const response = await api.get(`/uploads/${id}/file`, {
      params: { download: download ? '1' : undefined },
      responseType: 'blob',
    });
    openBlobResponse(response, fallbackName, download);
  },
};

export default api;
